import { base64URLStringToBuffer, bufferToBase64URLString } from '../utils/base64Utils';

// Utility function to detect iPad devices
const isIPad = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIPadOS = /ipad/.test(userAgent);
  const isMacWithTouch = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isSafariOnIPad = /safari/.test(userAgent) && !/chrome/.test(userAgent) && navigator.maxTouchPoints > 1;
  
  return isIPadOS || isMacWithTouch || isSafariOnIPad;
};

interface WebAuthnOptions {
  companyName: string;
  email: string;
}

interface AuthenticatorAssertionResponse extends AuthenticatorResponse {
  authenticatorData: ArrayBuffer;
  signature: ArrayBuffer;
  userHandle: ArrayBuffer | null;
}

interface AuthenticatorAttestationResponse extends AuthenticatorResponse {
  attestationObject: ArrayBuffer;
  getTransports?: () => string[];
}

interface AuthenticationResponseJSON {
  id: string;
  rawId: string;
  type: string;
  response: {
    authenticatorData: string;
    clientDataJSON: string;
    signature: string;
    userHandle: string | null;
  };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
}

export const webAuthnApi = {
  async getAuthenticationOptions() {
    
    const response = await fetch('https://auth.ts1cloud.com/auth/webauthn/login/options', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Origin': window.location.origin
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get authentication options' }));
      throw new Error(error.message || 'Failed to get authentication options');
    }

    const data = await response.json();

    if (!data.challenge) {
      throw new Error('Server response missing challenge');
    }

    const filteredCredentials = data.allowCredentials || [];

    const options = {
      challenge: base64URLStringToBuffer(data.challenge),
      timeout: data.timeout || 300000, // 5 minutes timeout
      rpId: window.location.hostname.split(':')[0], // Remove port if present
      allowCredentials: filteredCredentials.map((cred: any) => ({
        ...cred,
        id: base64URLStringToBuffer(cred.id),
        type: 'public-key'
      })),
      userVerification: 'required' as UserVerificationRequirement,
      // Add iPad-specific configuration
      ...(isIPad() && {
        // For iPads, ensure we're using platform authenticator
        extensions: {
          appid: window.location.origin,
          // Add additional iPad-specific extensions if needed
          credProps: true
        }
      })
    };

    return options;
  },

  async verifyAuthentication(verificationBody: any) {
    const verifyResponse = await fetch('https://auth.ts1cloud.com/auth/webauthn/login/verify', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Origin': window.location.origin
      },
      credentials: 'include',
      body: JSON.stringify(verificationBody)
    });

    if (!verifyResponse.ok) {
      const errorData = await verifyResponse.json().catch(() => ({ message: 'Authentication failed' }));
      throw new Error(errorData.message || 'Failed to verify authentication');
    }

    return verifyResponse.json();
  },

  prepareAuthenticationCredential(credential: PublicKeyCredential): any {
    const response = credential.response as AuthenticatorAssertionResponse;
    
    const preparedCredential = {
      response: {
        id: credential.id,
        rawId: bufferToBase64URLString(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: bufferToBase64URLString(response.authenticatorData),
          clientDataJSON: bufferToBase64URLString(response.clientDataJSON),
          signature: bufferToBase64URLString(response.signature)
        }
      }
    };
    
    return preparedCredential;
  },

  async getRegistrationOptions(options: WebAuthnOptions) {

    const response = await fetch('https://auth.ts1cloud.com/auth/webauthn/register/options', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        companyName: options.companyName,
        email: options.email
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to get registration options');
    }

    const data = await response.json();

    if (!data.challenge) {
      throw new Error('Server response missing challenge');
    }
    
    // Convert base64url strings to ArrayBuffer
    const challengeBuffer = base64URLStringToBuffer(data.challenge);
    // Convert user ID to UTF-8 encoded ArrayBuffer
    const userIdBuffer = new TextEncoder().encode(data.user?.id || '').buffer;

    if (!challengeBuffer) {
      throw new Error('Failed to process challenge');
    }

    // Prepare options for navigator.credentials.create
    const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
      challenge: challengeBuffer,
      rp: data.rp,
      user: {
        id: userIdBuffer,
        name: options.email,
        displayName: options.email
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' },  // ES256
        { alg: -257, type: 'public-key' } // RS256
      ],
      timeout: 60000,
      attestation: data.attestation || 'direct',
      excludeCredentials: [],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        residentKey: 'preferred',
        userVerification: 'required'
      },
      // credProps tells the authenticator to return credential properties (valid for registration)
      // Note: appid is authentication-only (FIDO U2F compat) and must NOT be used here
      ...(isIPad() && {
        extensions: {
          credProps: true
        }
      })
    };

    return publicKeyCredentialCreationOptions;
  },

  async verifyRegistration(options: WebAuthnOptions, credential: any) {
    const requestBody = {
      companyName: options.companyName,
      email: options.email,
      response: credential
    };

    const response = await fetch('https://auth.ts1cloud.com/auth/webauthn/register/verify', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to verify registration' }));
      throw new Error(error.message || 'Failed to verify registration');
    }

    return response.json();
  },

  prepareCredentialForVerify(credential: PublicKeyCredential) {
    const response = credential.response as AuthenticatorAttestationResponse;

    // Validate required fields
    if (!credential.rawId || !credential.id) {
      throw new Error('Invalid credential: missing ID or rawId');
    }

    if (!response.attestationObject || !response.clientDataJSON) {
      throw new Error('Invalid authenticator response: missing required data');
    }

    // Create the properly formatted credential object
    return {
      id: credential.id,
      rawId: bufferToBase64URLString(credential.rawId),
      type: "public-key",
      response: {
        clientDataJSON: bufferToBase64URLString(response.clientDataJSON),
        attestationObject: bufferToBase64URLString(response.attestationObject)
      }
    };
  }
};